// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  IconButton,
  Select,
  Button,
  Column,
  Row,
  DeleteIcon,
  Combobox,
  PlusIcon,
  TextInput,
  Tooltip,
} from "@hightouch/htui";
import { FC, Fragment } from "react";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";

import { AndOrToggleButton } from "src/components/explore/visual/condition-buttons";
import { GroupIndicatorBar } from "src/components/explore/visual/group-indicator-bar";
import { useModelQuery } from "src/graphql";
import { colors } from "src/components/explore/visual/colors";
import {
  RawColumn,
  RelatedColumn,
  ConditionType,
  ColumnType,
  OperatorOptionsWithIsIn,
  filterSupportedOperators,
} from "src/types/visual";

// Only operators that the backend `compileConditionFragment` can compile to SQL.
// Using an allowlist so new operators added upstream are excluded by default.
const SUPPORTED_CATALOG_OPERATORS = new Set([
  "=",
  "<>",
  ">",
  "<",
  ">=",
  "<=",
  "contains",
  "notcontains",
  "startswith",
  "notstartswith",
  "endswith",
  "notendswith",
  "isin",
  "notisin",
]);

function getCatalogOperatorOptions(
  propertyType?: string,
  connectionType?: string,
) {
  const options =
    propertyType && propertyType in OperatorOptionsWithIsIn
      ? OperatorOptionsWithIsIn[propertyType as ColumnType]
      : OperatorOptionsWithIsIn[ColumnType.String];
  return options
    .filter(filterSupportedOperators(connectionType))
    .filter((op) => SUPPORTED_CATALOG_OPERATORS.has(op.value));
}

export const CatalogFilter: FC<{
  leftModelId?: string | number;
  rightModelId?: string | number;
  connectionType?: string;
  formKey?: string;
}> = ({ leftModelId, rightModelId, connectionType, formKey = "filter" }) => {
  const { watch } = useFormContext();
  const type = watch(`${formKey}.type`);
  const { fields, remove, append } = useFieldArray({
    name: `${formKey}.conditions`,
  });

  return (
    <Column gap={4}>
      {fields.map((field, i) => (
        <Fragment key={field.id}>
          <Row>
            <GroupIndicatorBar conditionType={type} disableDrag />
            <Row
              flex={1}
              px={4}
              py={2}
              align="center"
              gap={4}
              bg="white"
              border="var(--chakra-border-hairline) solid"
              borderLeft="none"
              borderColor="base.border"
              borderTopRightRadius="md"
              borderBottomRightRadius="md"
            >
              <Condition
                leftModelId={leftModelId}
                rightModelId={rightModelId}
                connectionType={connectionType}
                index={i}
                formKey={formKey}
              />
              <IconButton
                variant="danger"
                aria-label="Delete condition"
                icon={DeleteIcon}
                onClick={() => remove(i)}
              />
            </Row>
          </Row>
          {fields.length > 0 && (
            <Controller
              name={`${formKey}.type`}
              render={({ field }) => (
                <AndOrToggleButton
                  conditionType={field.value}
                  onClick={() => {
                    field.onChange(field.value === "and" ? "or" : "and");
                  }}
                />
              )}
            />
          )}
        </Fragment>
      ))}

      <Row>
        <Controller
          name={`${formKey}.type`}
          render={({ field }) => (
            <AddConditionButton
              type={field.value || "and"}
              isDisabled={leftModelId == null}
              onClick={() => {
                if (!field.value) {
                  field.onChange("and");
                }
                append({
                  type: ConditionType.Property,
                  property: {
                    type: "raw",
                    modelId: String(leftModelId),
                    name: "",
                  } as RawColumn,
                  operator: "=",
                  value: "",
                  propertyType: "unknown",
                });
              }}
            />
          )}
        />
      </Row>
    </Column>
  );
};

const Condition = ({
  leftModelId,
  rightModelId,
  connectionType,
  index,
  formKey,
}: {
  leftModelId?: string | number;
  rightModelId?: string | number;
  connectionType?: string;
  index: number;
  formKey: string;
}) => {
  const leftQuery = useModelQuery(
    { id: leftModelId?.toString() ?? "" },
    { enabled: Boolean(leftModelId), select: (data) => data.segments_by_pk },
  );
  const rightQuery = useModelQuery(
    { id: String(rightModelId) },
    { enabled: Boolean(rightModelId), select: (data) => data.segments_by_pk },
  );

  const { setValue, watch } = useFormContext();
  const propertyType = watch(`${formKey}.conditions.${index}.propertyType`);

  return (
    <>
      <Controller
        name={`${formKey}.conditions.${index}.property`}
        render={({ field, fieldState: { error } }) => (
          <Combobox
            isInvalid={Boolean(error)}
            width="100%"
            value={field.value?.name}
            onChange={(name) => {
              const column = leftQuery.data?.columns.find(
                (col) => col.name.toLowerCase() === name,
              );
              field.onChange({
                type: "raw",
                modelId: String(leftModelId),
                name,
              } as RawColumn);
              setValue(
                `${formKey}.conditions.${index}.propertyType`,
                column?.type,
              );
              const newOptions = getCatalogOperatorOptions(
                column?.type,
                connectionType,
              );
              const currentOperator = watch(
                `${formKey}.conditions.${index}.operator`,
              );
              if (!newOptions.some((op) => op.value === currentOperator)) {
                setValue(
                  `${formKey}.conditions.${index}.operator`,
                  newOptions[0]?.value ?? "=",
                );
              }
            }}
            isLoading={leftQuery.isLoading}
            // Use lowercase names because all features are lowercased in the recommender
            options={
              leftQuery.data?.columns?.map((col) => ({
                ...col,
                name: col.name.toLowerCase(),
              })) ?? []
            }
            optionValue={(option) => option.name}
            optionLabel={(option) => option.alias ?? option.name}
          />
        )}
      />
      <Controller
        name={`${formKey}.conditions.${index}.operator`}
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            width="100%"
            isInvalid={Boolean(error)}
            options={getCatalogOperatorOptions(propertyType, connectionType)}
          />
        )}
      />
      <Controller
        name={`${formKey}.conditions.${index}.value`}
        render={({ field, fieldState: { error } }) => {
          if (typeof field.value !== "object" || field.value === null) {
            return (
              <>
                {rightModelId && (
                  <Select
                    value="static value"
                    width="auto"
                    onChange={() => {
                      field.onChange({
                        type: "column",
                        property: {
                          type: "related",
                          path: [],
                          column: {
                            type: "raw",
                            modelId: String(rightModelId),
                            name: "",
                          },
                        } as RelatedColumn,
                        propertyType: "unknown",
                      });
                    }}
                    options={["user property", "static value"]}
                    optionValue={(option) => option}
                    optionLabel={(option) => option}
                  />
                )}
                <TextInput
                  isInvalid={Boolean(error)}
                  width="100%"
                  value={String(field.value ?? "")}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (
                      propertyType === ColumnType.Number ||
                      propertyType === ColumnType.BigInt
                    ) {
                      const parsed = Number(val);
                      field.onChange(
                        val === "" || Number.isNaN(parsed) ? val : parsed,
                      );
                    } else {
                      field.onChange(val);
                    }
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </>
            );
          }
          // Use lowercase names because all features are lowercased in the recommender
          const options =
            (rightQuery.data?.parent
              ? rightQuery.data.parent.columns
              : rightQuery.data?.columns
            )?.map((col) => ({
              ...col,
              name: col.name.toLowerCase(),
            })) ?? [];

          const currentName =
            field.value?.type === "column"
              ? field.value.property.column?.name
              : field.value?.name;

          return (
            <>
              {rightModelId && (
                <Select
                  width="auto"
                  value="user property"
                  onChange={() => {
                    field.onChange("");
                  }}
                  options={["user property", "static value"]}
                  optionValue={(option) => option}
                  optionLabel={(option) => option}
                />
              )}
              <Combobox
                width="100%"
                isInvalid={Boolean(error)}
                isLoading={rightQuery.isLoading}
                value={currentName}
                onChange={(name) => {
                  const option = options.find((o) => o.name === name);
                  field.onChange({
                    type: "column",
                    property: {
                      type: "related",
                      path: [],
                      column: {
                        type: "raw",
                        modelId: String(rightModelId),
                        name,
                      },
                    } as RelatedColumn,
                    propertyType: option?.type,
                  });
                }}
                options={options}
                optionValue={(option) => option.name}
                optionLabel={(option) => option.alias ?? option.name}
              />
            </>
          );
        }}
      />
    </>
  );
};

function AddConditionButton({
  type,
  onClick,
  isDisabled,
}: {
  type: string;
  onClick: () => void;
  isDisabled?: boolean;
}) {
  return (
    <Tooltip
      message="Select a catalog to begin filtering"
      isDisabled={!isDisabled}
    >
      <Row
        as={Button}
        isDisabled={isDisabled}
        icon={PlusIcon}
        alignItems="center"
        borderWidth="hairline"
        borderStyle="solid"
        borderColor={colors.hover[type]}
        sx={{
          bg: colors.base[type],
          color: colors.text[type],
          _hover: {
            borderColor: colors.hover[type],
            bg: colors.hover[type],
            color: colors.textHover[type],
            svg: { color: colors.textHover[type] },
          },
          _active: {
            bg: colors.hover[type],
          },
          "&[aria-expanded=true]": {
            boxShadow: "none",
            _focusVisible: {
              boxShadow: "outline",
            },
          },
          svg: {
            color: colors.text[type],
            height: "20px",
            width: "20px",
          },
          _disabled: {
            color: "text.tertiary",
            bg: colors.base[type],
            svg: {
              color: "text.tertiary",
            },
            _hover: {
              color: "text.tertiary",
              svg: {
                color: "text.tertiary",
              },
              bg: colors.base[type],
            },
          },
        }}
        onClick={onClick}
      >
        Add catalog filter
      </Row>
    </Tooltip>
  );
}
